import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildFilesUrl } from '../config/api';

interface FileUploadProps {
  userId?: number;
  onUpload?: (file: { key: string; filename: string; size: number; url: string | null }) => void;
  onNarrative?: (message: string, type: 'info' | 'success' | 'error') => void;
  onUploadComplete?: () => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onUpload, onNarrative, onUploadComplete }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isPublic, setIsPublic] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      onNarrative?.(`📁 Selected: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`, 'info');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    if (!token) {
      alert('You must be logged in to upload files');
      return;
    }

    console.log('Starting upload:', {
      file: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type,
      isPublic,
      hasToken: !!token
    });

    setUploading(true);
    setUploadProgress(0);
    onNarrative?.('📤 Initiating file upload to CloudFlare R2...', 'info');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('public', isPublic.toString());

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const uploadUrl = buildFilesUrl('/files');
      console.log('Uploading to:', uploadUrl);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      console.log('Response status:', response.status);

      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        console.error('Upload failed:', data);
        throw new Error(data.error || 'Upload failed');
      }

      onNarrative?.(`✅ File uploaded successfully! Key: ${data.file.key}`, 'success');
      onUpload?.(data.file);
      onUploadComplete?.(); // Trigger refresh
      
      // Reset
      setSelectedFile(null);
      setIsPublic(false);
      setUploadProgress(0);
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      onNarrative?.(
        `❌ Upload failed: ${errorMessage}`,
        'error'
      );
      alert(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">📤 R2 File Upload</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select File
          </label>
          <input
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
            className="block w-full text-sm text-gray-900
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-600 file:text-white
              hover:file:bg-blue-700
              file:cursor-pointer"
          />
        </div>

        {selectedFile && (
          <div className="bg-gray-50 rounded p-3">
            <p className="text-sm text-gray-700">
              <strong>File:</strong> {selectedFile.name}
            </p>
            <p className="text-sm text-gray-700">
              <strong>Size:</strong> {(selectedFile.size / 1024).toFixed(2)} KB
            </p>
            <p className="text-sm text-gray-700">
              <strong>Type:</strong> {selectedFile.type || 'Unknown'}
            </p>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="public"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            className="rounded border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="public" className="text-sm text-gray-700">
            Make file publicly accessible
          </label>
        </div>

        {uploading && (
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-green-600 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          className={`w-full py-2 px-4 rounded-lg font-bold transition-all flex items-center justify-center space-x-2 ${
            !selectedFile || uploading
              ? 'bg-gray-400 cursor-not-allowed opacity-75'
              : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
          } text-white`}
        >
          {uploading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Uploading to R2...</span>
            </>
          ) : (
            <>
              <span>🚀</span>
              <span>Upload to R2</span>
            </>
          )}
        </button>
      </div>

      <div className="mt-4 text-xs text-gray-600">
        Files are stored in CloudFlare R2 with global replication
      </div>
    </div>
  );
};